import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { firstValueFrom } from 'rxjs';

import { BaseListComponent } from '../../../../shared/components/base-list.component';
import { CatalogTableComponent } from '../../../../shared/components/catalog-table/catalog-table.component';
import { PricesService } from '../../../../core/services/prices.service';
import {
  SelectFilterOptionsRequest,
  SelectFilterOptionsResponse,
  TableColumn,
  TableUserManualConfig
} from '../../../../core/models/catalog-table.model';
import { Price } from '../../../../core/models/price.model';
import { LoadingService } from '../../../../core/services/loading.service';
import { TimezonesService } from '../../../../core/services/timezones.service';
import { CommercialZonesService } from '../../../../core/services/commercial-zones.service';
import { CommercialConditionsService } from '../../../../core/services/commercial-conditions.service';
import { AuthService } from '../../../../core/services/auth.service';

@Component({
  selector: 'app-prices-list',
  standalone: true,
  imports: [
    CommonModule,
    CatalogTableComponent,
    ToastModule,
    ConfirmDialogModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './prices-list.component.html',
  styleUrl: './prices-list.component.scss'
})
export class PricesListComponent extends BaseListComponent<Price> {

  private pricesService = inject(PricesService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private confirmationService = inject(ConfirmationService);
  private loadingServiceInst = inject(LoadingService);
  private timezonesService = inject(TimezonesService);
  private commercialZonesService = inject(CommercialZonesService);
  private commercialConditionsService = inject(CommercialConditionsService);

  protected override listStateKey = 'price-configuration';
  protected override errorMessage = 'Error al cargar los precios';

  readonly pricesUserManual: TableUserManualConfig = {
    title: 'Manual de Ayuda: Configuración de Precios',
    embedUrl: 'https://scribehow.com/embed/Configuracion_de_Precios__zarBPss-RQGKoCk4xjuTcw?skipIntro=true&as=video'
  };

  // Adapter para BaseListComponent
  protected service = {
    getAll: (
      page: number,
      perPage: number,
      filters: Record<string, unknown>,
      sortField?: string,
      sortOrder?: number
    ) => this.pricesService.getAll(page, perPage, filters as Record<string, any>, sortField, sortOrder),
    delete: (id: number) => this.pricesService.delete(id)
  };

  protected canAdd = computed(() => this.authService.hasPermission('prices.add'));
  protected canEdit = computed(() => this.authService.hasPermission('prices.edit'));

  // Duplicar abre el formulario de alta, por lo que requiere el permiso de alta
  protected tableActions = computed(() => ({
    edit: this.canEdit(),
    delete: false,
    customActions: this.canAdd()
      ? [
          {
            actionKey: 'duplicate',
            icon: 'pi pi-copy',
            label: 'Duplicar',
            tooltip: 'Duplicar configuración',
            severity: 'secondary' as const
          }
        ]
      : []
  }));

  columns: TableColumn[] = [
    {
      field: 'name',
      header: 'Nombre',
      sortable: true,
      filterType: 'text',
      filterField: 'name',
      filterPlaceholder: 'Buscar nombre...',
      type: 'text'
    },
    {
      field: 'timezone_name',
      header: 'Zona Horaria',
      sortable: true,
      sortField: 'timezone.name',
      type: 'text',
      filterType: 'select',
      filterField: 'timezone_id',
      filterOptionsProvider: (request) => this.loadTimezonesFilterPage(request),
      filterPlaceholder: 'Seleccionar zona horaria...'
    },
    {
      field: 'commercial_zone_name',
      header: 'Zona Comercial',
      sortable: true,
      sortField: 'commercialZone.name',
      type: 'text',
      filterType: 'select',
      filterField: 'commercial_zone_id',
      filterOptionsProvider: (request) => this.loadCommercialZonesFilterPage(request),
      filterPlaceholder: 'Seleccionar zona comercial...'
    },
    {
      field: 'commercial_condition_name',
      header: 'Condición Comercial',
      sortable: true,
      sortField: 'commercialCondition.name',
      type: 'text',
      filterType: 'select',
      filterField: 'commercial_condition_id',
      filterOptionsProvider: (request) => this.loadCommercialConditionsFilterPage(request),
      filterPlaceholder: 'Seleccionar condición...'
    },
    {
      field: 'validity_display',
      header: 'Vigencia',
      sortable: true,
      sortField: 'validityStartDate',
      type: 'text',
      filterType: 'date',
      filterField: 'validity_start_date',
      filterDateConfig: { view: 'date', dateFormat: 'yy-mm-dd' }
    },
    {
      field: 'is_active',
      header: 'Estado',
      sortable: true,
      width: '200px',
      sortField: 'isActive',
      filterType: 'select',
      filterField: 'is_active',
      filterOptions: [
        { label: 'Activo', value: true },
        { label: 'Inactivo', value: false }
      ],
      type: 'boolean',
      booleanConfig: { trueText: 'Activo', falseText: 'Inactivo' }
    }
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.showToastFromCurrentNavigation(this.router);
  }

  private async loadTimezonesFilterPage(request: SelectFilterOptionsRequest): Promise<SelectFilterOptionsResponse> {
    const filters = request.search ? { name: request.search } : {};
    const res = await firstValueFrom(this.timezonesService.getAll(request.page, request.size, filters, 'name', 1));
    return {
      options: res.data.map(item => ({ label: item.name, value: item.id })),
      hasMore: request.page < res.meta.total_pages
    };
  }

  private async loadCommercialZonesFilterPage(request: SelectFilterOptionsRequest): Promise<SelectFilterOptionsResponse> {
    const filters = request.search ? { name: request.search } : {};
    const res = await firstValueFrom(this.commercialZonesService.getAll(request.page, request.size, filters, 'name', 1));
    return {
      options: res.data.map(item => ({ label: item.name, value: item.id })),
      hasMore: request.page < res.meta.total_pages
    };
  }

  private async loadCommercialConditionsFilterPage(request: SelectFilterOptionsRequest): Promise<SelectFilterOptionsResponse> {
    const filters = request.search ? { name: request.search } : {};
    const res = await firstValueFrom(this.commercialConditionsService.getAll(request.page, request.size, filters, 'name', 1));
    return {
      options: res.data.map(item => ({ label: item.name, value: item.id })),
      hasMore: request.page < res.meta.total_pages
    };
  }

  handleAdd(): void {
    this.router.navigate(['/price-configuration/new']);
  }

  handleEdit(item: Price): void {
    this.router.navigate(['/price-configuration/edit', item.id]);
  }

  handleDelete(item: Price): void {
    this.confirmationService.confirm({
      header: 'Confirmar Eliminación',
      message: `¿Está seguro de que desea eliminar el precio "${item.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteItemWithLoading(item.id, {
          successMessage: 'Precio eliminado exitosamente',
          errorMessage: 'Error al eliminar el precio'
        });
      }
    });
  }

  handleCustomAction(event: { action: string; item: Price }): void {
    if (event.action === 'duplicate') {
      this.onDuplicate(event.item);
    }
  }

  onDuplicate(item: Price): void {
    this.router.navigate(['/price-configuration/new'], { queryParams: { duplicateFrom: item.id } });
  }
}

